import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { pluralCategory } from './plural';

import en from './locales/en';
import zhCN from './locales/zh-CN';
import zhTW from './locales/zh-TW';
import ja from './locales/ja';
import ko from './locales/ko';
import fr from './locales/fr';
import de from './locales/de';
import it from './locales/it';
import es from './locales/es';
import ptPT from './locales/pt-PT';
import nl from './locales/nl';
import uk from './locales/uk';
import pl from './locales/pl';

// The admin UI languages, mirroring Shopify admin's own language list (the
// same set the reference apps offer). Labels are endonyms — each language is
// shown in itself, never translated — and the list order matches Shopify's.
export const ADMIN_LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'zh-CN', label: '简体中文' },
  { value: 'zh-TW', label: '繁體中文' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'es', label: 'Español' },
  { value: 'pt-PT', label: 'Português (Portugal)' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'uk', label: 'Українська' },
  { value: 'pl', label: 'Polski' }
];

const DICTS = {
  en,
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  ja,
  ko,
  fr,
  de,
  it,
  es,
  'pt-PT': ptPT,
  nl,
  uk,
  pl
};

const STORAGE_KEY = 'fc_admin_locale';

function storedLocale() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return DICTS[v] ? v : null;
  } catch {
    return null;
  }
}

// Module-level current dictionary so plain functions (the plan form's
// initialFormState defaults, useApi error strings) can translate without a
// hook. I18nProvider keeps these in sync with the rendered locale; they are
// initialized from localStorage so the first paint is already localized.
let currentLocale = storedLocale() || 'en';
let currentDict = DICTS[currentLocale];

function interpolate(str, params) {
  if (!params) return str;
  return str.replace(/\{(\w+)\}/g, (match, name) =>
    params[name] != null ? String(params[name]) : match
  );
}

// t('plans.updateFailed', { message: err.message }) — plain string lookup.
// Missing translations fall back to English, then to the key itself (a bare
// key on screen means a typo or an untranslated new string — loud on purpose).
export function t(key, params) {
  const str = currentDict[key] ?? en[key] ?? key;
  return interpolate(str, params);
}

// tp('plans.showing', 3) → "Showing 3 plans": appends the CLDR plural
// category for the current locale to the key and injects {count}.
export function tp(key, count, params) {
  const category = pluralCategory(currentLocale, count);
  const str =
    currentDict[`${key}_${category}`] ??
    currentDict[`${key}_other`] ??
    en[`${key}_${category}`] ??
    en[`${key}_other`] ??
    key;
  return interpolate(str, { ...params, count });
}

export function getCurrentLocale() {
  return currentLocale;
}

const I18nContext = createContext({ locale: 'en', setLocale: () => {} });

// useI18n 是组件订阅语言切换的方式:返回当前 locale(变化触发重渲染)和
// 模块级 t/tp(调用时读取当前词典)。组件必须经过本 hook 拿 t,而不是直接
// import 模块函数 —— 否则语言切换时不会重渲染(上下文订阅决定了更新范围)。
// 无法用 hook 的普通函数(表单默认值等)仍直接 import t,它们只在订阅了
// 上下文的组件渲染过程中被调用,时序上总是安全的。
export function useI18n() {
  return useContext(I18nContext);
}

// Mounted once at the app root (all auth branches) — owns the active admin
// language. Changing it swaps every dictionary consumer on the next render,
// mirrors the choice into <html lang> and localStorage (the instant-paint
// cache and the fallback for local demo / support-impersonation sessions,
// which have no per-shop settings of their own).
export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(currentLocale);

  const setLocale = useCallback((next) => {
    if (!DICTS[next] || next === currentLocale) return;
    currentLocale = next;
    currentDict = DICTS[next];
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // private mode etc. — the choice just won't survive a reload
    }
    setLocaleState(next);
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t, tp }), [locale, setLocale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
